class ServiceSelector extends HTMLElement {
  constructor() {
    super();
    this.selectedService = null;
    this.bookNowButton = null;
  }

  connectedCallback() {
    // Use MutationObserver to wait for service items to be available
    this.waitForServices();
  }

  waitForServices() {
    try {
      // Render immediately since we now get data from attributes
      this.render();
      this.setupEventListeners();
      this.selectFirstService();
    } catch (error) {
      console.error('ServiceSelector: Error in waitForServices:', error);
      this.style.display = 'none';
    }
  }

  render() {
    try {
      const services = this.getServices();
      
      if (services.length === 0) {
        this.style.display = 'none';
        return;
      }

      this.innerHTML = `
        <div class="service-selector">
          <div class="service-options">
            ${services.map((service, index) => {
              const hasValidService = service.id || service.url;
              return `
                <div class="service-option ${hasValidService ? 'has-valid-service' : ''}" data-service-id="${service.id || ''}" data-service-url="${service.url || ''}">
                  <div class="service-option-content">
                    <div class="service-name">${service.name}</div>
                    <div class="service-price">${service.price}</div>
                  </div>
                  <div class="service-option-indicator"></div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    } catch (error) {
      console.error('ServiceSelector: Error in render:', error);
      this.style.display = 'none';
    }
  }

  getServices() {
    const services = [];
    
    // Get service data from data-services attribute
    const servicesData = this.getAttribute('data-services');
    
    if (servicesData) {
      try {
        const parsedServices = JSON.parse(servicesData);
        
        if (Array.isArray(parsedServices)) {
          services.push(...parsedServices);
        }
      } catch (error) {
        console.error('ServiceSelector: Error parsing services data:', error);
      }
    }
    
    // Fallback: try to get from DOM if data-services is not available
    if (services.length === 0) {
      const serviceItems = this.querySelectorAll('.service-item');
      
      if (serviceItems.length > 0) {
        serviceItems.forEach((item, index) => {
          const nameElement = item.querySelector('.service-name');
          const priceElement = item.querySelector('.service-price');
          const linkElement = item.querySelector('.service-link');
          
          if (nameElement && priceElement) {
            const name = nameElement.textContent.trim();
            const price = priceElement.textContent.trim();
            const url = linkElement ? linkElement.href : '';
            const id = linkElement ? this.extractServiceId(linkElement.href) : '';
            
            services.push({ name, price, url, id });
          }
        });
      }
    }
    
    return services;
  }

  extractServiceId(url) {
    if (!url) return '';
    const match = url.match(/serviceid=([^&]+)/);
    return match ? match[1] : '';
  }

  setupEventListeners() {
    const options = this.querySelectorAll('.service-option');
    
    options.forEach(option => {
      // Only add click handlers to services with valid IDs or URLs
      if (option.classList.contains('has-valid-service')) {
        option.addEventListener('click', () => {
          this.selectService(option);
        });
      }
    });

    // Find the book now button
    this.bookNowButton = this.closest('.modal-slide')?.querySelector('.action-wrapper .button');
  }

  selectService(selectedOption) {
    // Check if this service has a valid ID or URL
    const serviceId = selectedOption.dataset.serviceId;
    const serviceUrl = selectedOption.dataset.serviceUrl;
    
    if (!serviceId && !serviceUrl) {
      return;
    }
    
    // Remove previous selection
    this.querySelectorAll('.service-option').forEach(option => {
      option.classList.remove('is-selected');
    });

    // Add selection to clicked option
    selectedOption.classList.add('is-selected');
    
    // Store selected service
    this.selectedService = {
      id: serviceId,
      url: serviceUrl,
      name: selectedOption.querySelector('.service-name').textContent
    };

    // Update book now button URL
    this.updateBookNowButton();
    
    // Dispatch custom event for other components to listen to
    this.dispatchEvent(new CustomEvent('serviceSelected', {
      detail: this.selectedService,
      bubbles: true
    }));
  }

  selectFirstService() {
    // Find the first service option that has a valid ID or URL
    const options = this.querySelectorAll('.service-option');
    for (let option of options) {
      const serviceId = option.dataset.serviceId;
      const serviceUrl = option.dataset.serviceUrl;
      
      if (serviceId || serviceUrl) {
        this.selectService(option);
        return;
      }
    }
    
    // If no valid services found, don't select anything
  }

  updateBookNowButton() {
    if (!this.bookNowButton || !this.selectedService) {
      return;
    }
    
    if (this.selectedService.url) {
      this.bookNowButton.href = this.selectedService.url;
    } else if (this.selectedService.id) {
      // Construct URL from service ID
      const baseUrl = 'https://pauledmonds.zenoti.com/webstoreNew/services/48e483cf-03ad-4672-9fb7-abf4c50a190b';
      const newUrl = `${baseUrl}?serviceid=${this.selectedService.id}`;
      this.bookNowButton.href = newUrl;
    }
  }

  getSelectedService() {
    return this.selectedService;
  }
}

// Register the web component
customElements.define('service-selector', ServiceSelector);
